import React, { Fragment, useContext, useState } from 'react';
import './NewHire.css';
import NewEmployeeInfo from '../EmployeeInfo/NewEmployeeInfo';
import EmployeePositionInfo from '../EmployeeInfo/EmployeePositionInfo';
import { useEffect } from 'react';
import CbmContext from '../../Context/Cbm/cbmContext';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';

const NewHire = ({
  setPayLoad,
  setIdBadgeFile,
  setGovIdFrontFile,
  setGovIdRearFile,
  setSsnCardFile,
  setFormError,
  errMsg,
}) => {
  const cbmContext = useContext(CbmContext);
  const {
    stores,
    portalUser,
    submittedData,
    errorCode,
    submitStatus,
    submitFailed,
    stateList,
    getStates,
    stateListRetrieved,
    auth,
    authLoading,
  } = cbmContext;

  const [page, setPage] = useState(1);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [selectedState, setSelectedState] = useState(null);

  const [newHireInfo, setNewHireInfo] = useState({
    hireType: '',
    employee: {
      employeeFirst: '',
      employeeMiddle: '',
      employeeLast: '',
      employeeSecondLast: '',
      address: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
      },
      phone1: '',
      phone2: '',
      email: '',
      ssn: '',
      gender: '',
      dob: '',
    },
    firstDayWorked: '',
    numWorkDays: 0,
    store: null,
    payRate: 0,
    position: '',
    positionNum: '',
    numOfHours: '',
    preferredLanguage: '',
    comments: '',
    documentTitle: '',
    documentNumber: '',
    aNumber: '',
    documentExp: '',
    portalUser,
  });

  useEffect(() => {
    // Restore scroll position after page content switches
    window.scrollTo({ top: lastScrollY, left: 0, behavior: 'instant' });
  }, [page, lastScrollY]);

  useEffect(() => {
    // Only fetch states if authenticated and not already retrieved
    if (
      selectedState === null &&
      stateListRetrieved === false &&
      auth &&
      !authLoading
    ) {
      getStates();
    }
  }, [selectedState, getStates, stateListRetrieved, auth, authLoading]);

  // Use unified form setup hook for validation and payload sync
  // Note: NewHire uses FormData for file uploads, so rehydration is handled separately
  const { validationErrors, getFieldError } = useFormSetup({
    formName: 'newHire',
    formData: newHireInfo,
    setFormData: setNewHireInfo,
    setPayLoad,
    setFormError,
    submittedData: null, // Handled by custom rehydration effect below for FormData
    errorCode,
  });

  // Custom rehydration for FormData-based form
  useEffect(() => {
    if (
      submittedData !== null &&
      errorCode !== '' &&
      submitStatus &&
      submitFailed
    ) {
      if (submittedData instanceof FormData) {
        submittedData
          .get('newHireInfoJSON')
          .text()
          .then((resp) => {
            if (resp) {
              setNewHireInfo(JSON.parse(resp));
              window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }
          });
      }
    }
  }, [submittedData, errorCode, submitFailed, submitStatus]);

  const handleAddressInfo = (e) => {
    const { name, value } = e.target;
    let currentAddressInfo = { ...newHireInfo };
    currentAddressInfo.employee.address[name] = value;
    setNewHireInfo(currentAddressInfo);
  };

  const validateFileUpload = (e) => {
    console.log(e.target.files);
    console.log(e.target.name);
    if (e.target.files.length > 0 && e.target.name === 'idBadge') {
      setIdBadgeFile(e.target.files[0]);
    } else if (e.target.files.length > 0 && e.target.name === 'govIdFront') {
      setGovIdFrontFile(e.target.files[0]);
    } else if (e.target.files.length > 0 && e.target.name === 'govIdRear') {
      setGovIdRearFile(e.target.files[0]);
    } else if (e.target.files.length > 0 && e.target.name === 'ssnCard') {
      setSsnCardFile(e.target.files[0]);
    }
  };

  const onHandleChange = (e) => {
    const { name, value } = e.target;
    let currentHireInfo = { ...newHireInfo };
    if (
      name === 'employeeFirst' ||
      name === 'employeeMiddle' ||
      name === 'employeeLast' ||
      name === 'employeeSecondLast' ||
      name === 'phone1' ||
      name === 'phone2' ||
      name === 'email' ||
      name === 'dob' ||
      name === 'ssn' ||
      name === 'gender'
    ) {
      currentHireInfo.employee[name] = value;
    } else {
      currentHireInfo[name] = value;
    }
    setNewHireInfo(currentHireInfo);
  };
  const changeStoreInfo = (e) => {
    const { value } = e.target;
    const foundStore = stores.filter((store) => {
      return parseInt(value) === store.id;
    });
    setNewHireInfo({ ...newHireInfo, store: foundStore[0] });
  };

  return (
    <Fragment>
      <div className="heading">
        <h1>New Hire</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      {page === 1 ? (
        <NewEmployeeInfo
          handleAddressInfo={handleAddressInfo}
          addressInfo={newHireInfo.employee.address}
          onHandleChange={onHandleChange}
          setNewHireInfo={setNewHireInfo}
          newHireInfo={newHireInfo}
          states={stateList}
          selectedState={selectedState}
          setSelectedState={setSelectedState}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      ) : (
        <EmployeePositionInfo
          onHandleChange={onHandleChange}
          changeStoreInfo={changeStoreInfo}
          storeList={stores}
          setIdBadgeFile={setIdBadgeFile}
          newHireInfo={newHireInfo}
          validateFileUpload={validateFileUpload}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      )}
      <div className="nextPrevBtns">
        {page === 1 ? (
          <div className="nextBtn">
            <button
              className="newHireNextPage"
              onClick={() => {
                setLastScrollY(window.scrollY || window.pageYOffset || 0);
                setPage(2);
              }}
            >
              Next page
            </button>
          </div>
        ) : (
          <Fragment>
            <div className="prevBtn">
              <button
                className="newHirePrevPage"
                onClick={() => {
                  setLastScrollY(window.scrollY || window.pageYOffset || 0);
                  setPage(1);
                }}
              >
                Prev page
              </button>
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default NewHire;
